import { FC, lazy, Suspense, useRef, useEffect } from 'react';
import { createBrowserRouter, Outlet, useLocation } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { withRoutePatterns } from '../utils/routePattern';
import { useOktaAuth } from '@okta/okta-react';
import { Page } from './Page';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TicketDetailsNoLocation from '../pages/Tickets/TicketDetailsNoLocation';
import { AppAuthWrapper } from './AppAuthWrapper';
import SessionTimeoutModal from '../components/Global/Modals/SessionTimer/SessionTimeoutModal';
import { CustomLoginCallback } from './CustomLoginCallback';
import { PageLoader as CorePageLoader } from '@cfa/react-core';
import PageNotFound from '../pages/PageNotFound/PageNotFound';
import GuideDeepLink from '../pages/Equipment/GuideDeepLink';
import LegacyRedirect from './LegacyRedirect';
import { themeSizes } from '../styles/globalStyleVars';

export const ChickenSpinner = () => (
  <div style={{ display: 'block' }}>
    <CorePageLoader style={{ margin: `${themeSizes.xxl} auto` }} />
  </div>
);

const Home = lazy(() => import('../pages/Home/Home'));
const Tickets = lazy(() => import('../pages/Tickets/Tickets'));
const TicketDetails = lazy(() => import('../pages/Tickets/TicketDetails'));
const FormsOverview = lazy(() => import('../pages/Forms/FormsOverview'));
const FormDetails = lazy(() => import('../pages/Forms/FormDetails'));
const Search = lazy(() => import('../pages/Search/Search'));
const KnowledgeArticleDetails = lazy(
  () => import('../pages/Knowledge/KnowledgeArticleDetails')
);

// Custom Suspense wrapper to avoid double loading with component-level loader
const KnowledgeArticleDetailsWithSuspense = () => (
  <Suspense fallback={<div />}>
    <KnowledgeArticleDetails />
  </Suspense>
);
const AssetFormExamples = lazy(
  () => import('../pages/Assets/AssetFormExamples')
);
const AppointmentsManagement = lazy(
  () => import('../pages/Appointments/AppointmentsManagement')
);
const AppointmentDetails = lazy(
  () => import('../pages/Appointments/AppointmentDetails')
);
const TroubleshootingBrowse = lazy(
  () => import('../components/Equipment/Troubleshooting/TroubleshootingBrowse')
);
const TroubleshootingBrowseWithSuspense = () => (
  <Suspense fallback={<div />}>
    <TroubleshootingBrowse />
  </Suspense>
);
const ErrorPage = lazy(() => import('../pages/Error/ErrorPage'));
const EquipmentOverview = lazy(
  () => import('../pages/Equipment/EquipmentOverview')
);
const EquipmentBrowse = lazy(
  () => import('../pages/Equipment/EquipmentBrowse')
);
const EquipmentDetails = lazy(
  () => import('../pages/Equipment/EquipmentDetails')
);
const Profile = lazy(() => import('../pages/Profile/Profile'));
const DownloadPage = lazy(() => import('../pages/Global/DownloadPage'));

const OktaAuthProtected: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation();
  const hasAttemptedRedirect = useRef(false);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      hasAttemptedRedirect.current = false;
      sessionStorage.removeItem('signout-in-progress');
      return;
    }

    if (
      authState &&
      !authState.isAuthenticated &&
      !authState.isPending &&
      !oktaAuth.isLoginRedirect() &&
      !hasAttemptedRedirect.current
    ) {
      hasAttemptedRedirect.current = true;

      try {
        oktaAuth.setOriginalUri(location.pathname + location.search);
        oktaAuth.signInWithRedirect();
      } catch (error) {
        console.error('Failed to initiate Okta sign-in redirect:', error);
        // Reset the flag so user can try again or handle the error appropriately
        hasAttemptedRedirect.current = false;
      }
    }
  }, [authState, oktaAuth, location]);

  if (
    !authState?.isAuthenticated ||
    authState === undefined ||
    authState.isPending
  ) {
    return <ChickenSpinner />;
  }

  return <>{children}</>;
};

//layout route that provides AppAuthWrapper, ScrollToTop, and SessionTimeoutModal
const AppLayout = () => (
  <AppAuthWrapper>
    <SessionTimeoutModal />
    <Suspense fallback={<ChickenSpinner />}>
      <Outlet />
    </Suspense>
  </AppAuthWrapper>
);

//feature flag wrappers for equipment routes
const TroubleshootingBrowseFlagWrapper = () => {
  const { ['equipment-tab']: equipmentTab } = useFlags();
  return (
    <Page
      component={equipmentTab && <TroubleshootingBrowseWithSuspense />}
      featureFlag={equipmentTab}
    />
  );
};
const EquipmentOverviewFlagWrapper = () => {
  const { ['equipment-tab']: equipmentTab } = useFlags();
  return (
    <Page
      component={equipmentTab && <EquipmentOverview />}
      featureFlag={equipmentTab}
    />
  );
};
const EquipmentBrowseFlagWrapper = () => {
  const { ['equipment-tab']: equipmentTab } = useFlags();
  return (
    <Page
      component={equipmentTab && <EquipmentBrowse />}
      featureFlag={equipmentTab}
    />
  );
};
const EquipmentDetailsFlagWrapper = () => {
  const { ['equipment-tab']: equipmentTab } = useFlags();
  return (
    <Page
      component={equipmentTab && <EquipmentDetails />}
      featureFlag={equipmentTab}
    />
  );
};

const routes: RouteObject[] = [
  {
    path: '/implicit/callback',
    element: <CustomLoginCallback />,
  },
  {
    path: '/authenticating',
    element: <ChickenSpinner />,
  },
  {
    element: <AppLayout />, //all main routes use this layout
    children: [
      {
        path: '/',
        element: (
          <OktaAuthProtected>
            <Page component={<Home />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/home',
        element: (
          <OktaAuthProtected>
            <Page component={<Home />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/tickets/:location?',
        element: (
          <OktaAuthProtected>
            <Page component={<Tickets />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/ticket/:id',
        element: (
          <OktaAuthProtected>
            <Page component={<TicketDetailsNoLocation />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/tickets/:location/:id',
        element: (
          <OktaAuthProtected>
            <Page component={<TicketDetails />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/forms',
        element: (
          <OktaAuthProtected>
            <Page component={<FormsOverview />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/forms/:id',
        element: (
          <OktaAuthProtected>
            <Page component={<FormDetails />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/kb/:id',
        element: (
          <OktaAuthProtected>
            <Page component={<KnowledgeArticleDetailsWithSuspense />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/search',
        element: (
          <OktaAuthProtected>
            <Page component={<Search />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/asset-form-examples',
        element: (
          <OktaAuthProtected>
            <Page component={<AssetFormExamples />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/appointments',
        element: (
          <OktaAuthProtected>
            <Page component={<AppointmentsManagement />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/appointments/:id',
        element: (
          <OktaAuthProtected>
            <Page component={<AppointmentDetails />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/equipment/:location/guides',
        element: (
          <OktaAuthProtected>
            <TroubleshootingBrowseFlagWrapper />
          </OktaAuthProtected>
        ),
      },
      // Renamed from /equipment/:location/troubleshooting - keep old links working.
      {
        path: '/equipment/:location/troubleshooting',
        element: (
          <OktaAuthProtected>
            <LegacyRedirect to='/equipment/:location/guides' />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/equipment/:location?',
        element: (
          <OktaAuthProtected>
            <EquipmentOverviewFlagWrapper />
          </OktaAuthProtected>
        ),
      },
      /**
       * Generic deep-link entry point for troubleshooting guides.
       * `/guide/:flowId?location=:location` captures the target flow, then
       * redirects to the equipment overview and auto-opens that flow's
       * troubleshooting drawer. The `location` query param is optional.
       */
      {
        path: '/guide/:flowId',
        element: (
          <OktaAuthProtected>
            <GuideDeepLink />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/equipment/:location/:category',
        element: (
          <OktaAuthProtected>
            <EquipmentBrowseFlagWrapper />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/equipment/:location/:category/:item',
        element: (
          <OktaAuthProtected>
            <EquipmentDetailsFlagWrapper />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/settings',
        element: (
          <OktaAuthProtected>
            <Page component={<Profile />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/error',
        element: (
          <OktaAuthProtected>
            <Page component={<ErrorPage />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '/download',
        element: (
          <OktaAuthProtected>
            <Page component={<DownloadPage />} />
          </OktaAuthProtected>
        ),
      },
      {
        path: '*',
        element: (
          <OktaAuthProtected>
            <Page component={<PageNotFound />} />
          </OktaAuthProtected>
        ),
      },
    ],
  },
];

// Each route carries its own path pattern on `handle` so components can
// rebuild the current URL with different params. See utils/routePattern.
export const router = createBrowserRouter(withRoutePatterns(routes));
